import { FaChurch } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from "react";
import baseURL from "../../config/api";
import { formatUSPhone } from "../../utils/phoneFormatter";
import useGroup from "../../hook/useGroup";
import Cookies from "js-cookie";

const Church = () => {
  const token = Cookies.get("token-my-cdir");
  const navigate = useNavigate();
  const { selectedGroupId, isSuperAdmin }: any = useGroup();
  const pathRoot = isSuperAdmin ? "superadmin" : "admin";
  const [searchTableData, setSearchTableData] = useState<any[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const addChurch = () => {
    navigate(`/${pathRoot}/church/addChurch`);
  }

  const editChurch = (id: string) => {
    navigate(`/${pathRoot}/church/editChurch/${id}`);
  }

  useEffect(() => {
    if (selectedGroupId) {
      setIsDataLoaded(true);
      fetch(
        `${baseURL}group/${selectedGroupId}`,
        {
          headers: {
            Authorization: `Bearer ${token?.toString()}`
          }
        }
      )
        .then((res) => res.json())
        .then((data) => {
          setSearchTableData([data]);
          setIsDataLoaded(false);
        })
        .catch((error) => {
          console.log(error);
          setIsDataLoaded(false);
          setSearchTableData([]);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId])

  const getAddress = (group: any) => {
    const addressParts = [
      group?.street,
      group?.street2,
      group?.city,
      group?.state,
      group?.country,
    ].filter(Boolean);

    const address = addressParts.join(", ");

    return group?.zip
      ? `${address} - ${group.zip}`
      : address;
  };


  return (
    <div className="w-full p-3 xm:p-4 overflow-x-hidden">
      <div className="mx-auto w-full">

        {/* HEADER */}
        <div className="min-w-full flex flex-col xm:flex-row justify-between items-start xm:items-center gap-3">
          <h1 className="px-2 xm:px-4 py-1.5 flex items-center gap-2">
            <FaChurch fontSize={20} />
            <span className="text-base xm:text-lg">Church</span>
          </h1>

          {isSuperAdmin && (
            <button
              className="bg-secondary hover:bg-custom-blue px-4 py-2 xm:py-1.5 rounded-sm text-white w-full xm:w-auto"
              onClick={addChurch}
            >
              Add Church
            </button>
          )}
        </div>

        {!isDataLoaded ? (
          <>
            {searchTableData.length > 0 ? (
              <div className="mt-5 space-y-[1.5rem]">
                {searchTableData.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="bg-white rounded-3xl shadow-xl border p-6 md:p-10"
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                        <div className="flex flex-col items-center text-center">
                          <ImageWithLoader imagePath={item?.imagePath} />

                          <h2 className="mt-5 text-2xl font-semibold text-gray-800">
                            {item.name}
                          </h2>
                        </div>

                        <div className="lg:col-span-2 space-y-8">
                          <div className="flex items-start gap-4">
                            <div className="bg-blue-100 text-secondary p-3 rounded-xl">
                              📍
                            </div>

                            <div>
                              <p className="text-sm text-gray-500">
                                Address
                              </p>

                              <p className="text-gray-800 font-medium break-words">
                                {getAddress(item)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-4">
                            <div className="bg-green-100 text-green-600 p-3 rounded-xl">
                              📞
                            </div>

                            <div>
                              <p className="text-sm text-gray-500">
                                Phone
                              </p>

                              <p className="text-gray-800 font-medium">
                                {formatUSPhone(item?.phoneNumber || "")}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-4">
                            <div className="bg-purple-100 text-purple-600 p-3 rounded-xl">
                              ✉️
                            </div>

                            <div>
                              <p className="text-sm text-gray-500">
                                Email
                              </p>

                              <p className="text-gray-800 font-medium break-all">
                                {item.emailAddress}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-4">
                            <div className="bg-orange-100 text-orange-600 p-3 rounded-xl">
                              🌐
                            </div>

                            <div>
                              <p className="text-sm text-gray-500">
                                Website
                              </p>

                              <a
                                href={
                                  item?.website?.startsWith("http")
                                    ? item.website
                                    : `https://${item.website}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-secondary font-medium break-all hover:underline"
                              >
                                {item.website || "N/A"}
                              </a>
                            </div>
                          </div>

                          <div className="flex gap-4 pt-2">
                            <button
                              type="button"
                              className="bg-secondary hover:bg-custom-blue text-white px-5 py-2 rounded-md"
                              onClick={() => editChurch(item.id)}
                            >
                              Edit Church
                            </button>
                          </div>

                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
              : (
                <div className="min-w-full h-[80px] flex justify-center items-center">
                  <span className="text-sm">No Data Found</span>
                </div>
              )}
          </>
        ) : (
          <div className="min-w-full h-[300px] xm:h-[480px] flex justify-center items-center">
            <div className="flex flex-col items-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-current border-r-transparent text-secondary"></div>
              <span className="text-sm mt-2">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Church

type ImageWithLoaderProps = {
  imagePath: string
}

const ImageWithLoader = ({ imagePath }: ImageWithLoaderProps) => {
  const [loading, setLoading] = useState<boolean>(true);

  return (
    <div className="relative w-44 h-44 md:w-56 md:h-56">
      {
        loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-gray-200 animate-pulse">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-current border-r-transparent text-secondary"></div>
            <span className="text-sm mt-2">Loading...</span>
          </div>
        )
      }

      <img
        src={
          imagePath
            ? `${baseURL}commonFile/Get?name=${imagePath}`
            : "/images/img_placeholder1.jpg"
        }
        alt="church"
        className={`w-44 h-44 md:w-56 md:h-56 rounded-2xl object-cover shadow-lg transition-opacity duration-300 ${loading ? "opacity-0" : "opacity-100"}`}
        onLoad={() => setLoading(false)}
        onError={(e: any) => {
          e.target.src = "/images/img_placeholder1.jpg";
          setLoading(false);
        }}
      />
    </div>
  );
}
